import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { Camera, Upload, X, Check, Loader2, User, Trash2, Bug } from 'lucide-react';
import { API } from '../config/api';
import ImageCropModal from './ImageCropModal';
import { safeJson } from '../utils/safeJson';

// DEBUG MODE - Set to true to show on-screen debugging panel
// Disabled by default, enable for troubleshooting upload issues
const DEBUG_MODE = false;

/**
 * ProfilePhotoUploader - Upload and change profile picture for ANY user (all roles)
 * 
 * Features:
 * - Image selection from device
 * - Square crop modal with zoom/rotate
 * - Upload progress indicator
 * - Remove photo option
 * - Works for all roles (Listener, Artist, etc.)
 * 
 * Props:
 * - currentImage: string - Current profile image URL (can be relative or absolute)
 * - onUploadSuccess: (newImageUrl: string) => void - Callback when upload succeeds
 * - getAuthHeaders: () => object - Function to get auth headers
 * - user: object - User object from auth context
 * - size: 'sm' | 'md' | 'lg' - Size of the avatar display
 * - showRemoveButton: boolean - Whether to show the remove button
 */
export default function ProfilePhotoUploader({
  currentImage,
  onUploadSuccess,
  getAuthHeaders,
  user,
  size = 'lg',
  showRemoveButton = true
}) {
  const [rawImage, setRawImage] = useState(null); // Original selected image
  const [croppedPreview, setCroppedPreview] = useState(null); // Cropped preview
  const [croppedBlob, setCroppedBlob] = useState(null); // Cropped blob for upload
  const [showCropModal, setShowCropModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  
  // DEBUG: On-screen debug log
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebugPanel, setShowDebugPanel] = useState(DEBUG_MODE);
  
  // Debug log helper - logs to console AND stores for on-screen display
  const debugLog = (message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = data ? `[${timestamp}] ${message}: ${JSON.stringify(data).substring(0, 200)}` : `[${timestamp}] ${message}`;
    console.log('[DEBUG]', logEntry);
    setDebugLogs(prev => [...prev.slice(-15), logEntry]); // Keep last 15 logs
  };
  
  // Store token locally when component mounts to ensure it's available
  const [localToken, setLocalToken] = useState(null);
  
  useEffect(() => {
    // On mount, try to get and store the token
    let token = null;
    
    debugLog('Component mounted, checking for tokens...');
    debugLog('Current hostname', window.location.hostname);
    debugLog('API URL configured as', API);
    
    // Try getAuthHeaders
    if (getAuthHeaders) {
      try {
        const headers = getAuthHeaders();
        if (headers.Authorization) {
          token = headers.Authorization.replace('Bearer ', '');
          debugLog('Token from getAuthHeaders', token ? `${token.substring(0, 20)}...` : 'null');
        }
      } catch (e) {
        debugLog('getAuthHeaders error', e.message);
      }
    }
    
    // Fallback to localStorage
    if (!token) {
      token = localStorage.getItem('token');
      debugLog('Token from localStorage', token ? `${token.substring(0, 20)}...` : 'null');
    }
    
    // Fallback to user prop
    if (!token && user?.access_token) {
      token = user.access_token;
      debugLog('Token from user prop', token ? `${token.substring(0, 20)}...` : 'null');
    }
    
    if (token) {
      setLocalToken(token);
      debugLog('✓ Token captured successfully');
    } else {
      debugLog('✗ No token available on mount');
    }
  }, [getAuthHeaders, user]);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  const buttonSizes = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-sm px-4 py-2'
  };

  // Build the full image URL for display
  const getDisplayUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // For relative URLs, prepend the API base (without /api suffix)
    const baseUrl = API.replace('/api', '');
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const fileExt = file.name.toLowerCase().split('.').pop();
    const isValidType = allowedTypes.includes(file.type) || 
                        ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(fileExt);

    if (!isValidType) {
      setError('Invalid file type. Please use JPG, PNG, or WEBP.');
      toast.error('Invalid file type. Please use JPG, PNG, or WEBP.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    // Read file and open crop modal
    const reader = new FileReader();
    reader.onload = (e) => {
      setRawImage(e.target.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);

    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = (blob, dataUrl) => {
    setCroppedBlob(blob);
    setCroppedPreview(dataUrl);
    setShowCropModal(false);
    setRawImage(null);
  };

  const handleCloseCropModal = () => {
    setShowCropModal(false);
    setRawImage(null);
  };

  const handleUpload = async () => {
    if (!croppedBlob) return;

    setUploading(true);
    setProgress(0);
    setError(null);
    
    // Clear previous debug logs and start fresh
    setDebugLogs([]);
    debugLog('=== UPLOAD STARTED ===');
    debugLog('Browser', navigator.userAgent.substring(0, 60));
    debugLog('Current URL', window.location.href);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 150);

      // Log the API URL being used
      debugLog('API Base URL', API);
      debugLog('Upload endpoint', `${API}/upload/profile-photo`);
      
      // STEP 1: Get a completely fresh token by calling /auth/me
      // This bypasses any localStorage/context issues
      let currentToken = null;
      
      // First try to get an existing token from any source
      debugLog('--- Checking token sources ---');
      
      if (getAuthHeaders) {
        try {
          const headers = getAuthHeaders();
          if (headers.Authorization) {
            currentToken = headers.Authorization.replace('Bearer ', '');
            debugLog('✓ Token from getAuthHeaders', currentToken.substring(0, 25) + '...');
          }
        } catch (e) {
          debugLog('✗ getAuthHeaders error', e.message);
        }
      }
      
      if (!currentToken) {
        try {
          currentToken = localStorage.getItem('token');
          debugLog('localStorage token', currentToken ? currentToken.substring(0, 25) + '...' : 'NOT FOUND');
        } catch (e) {
          debugLog('✗ localStorage access error', e.message);
        }
      }
      
      if (!currentToken && user?.access_token) {
        currentToken = user.access_token;
        debugLog('✓ Token from user prop', currentToken.substring(0, 25) + '...');
      }
      
      // STEP 2: Use that token to get a FRESH token from /auth/me
      if (currentToken) {
        debugLog('--- Refreshing token via /auth/me ---');
        try {
          const meUrl = `${API}/auth/me`;
          debugLog('Calling', meUrl);
          
          const meResponse = await fetch(meUrl, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${currentToken}` },
            credentials: 'include'
          });
          
          debugLog('/auth/me response status', meResponse.status);
          debugLog('/auth/me response ok', meResponse.ok);
          
          if (meResponse.ok) {
            const meData = await meResponse.json();
            debugLog('/auth/me returned user', meData.email || meData.name || 'unknown');
            
            if (meData.access_token) {
              currentToken = meData.access_token;
              debugLog('✓ Got FRESH token from /auth/me', currentToken.substring(0, 25) + '...');
              // Save to localStorage for future use
              try {
                localStorage.setItem('token', currentToken);
                debugLog('✓ Saved fresh token to localStorage');
              } catch (e) {
                debugLog('✗ Could not save to localStorage', e.message);
              }
            } else {
              debugLog('⚠ /auth/me did not return access_token');
            }
          } else {
            const errText = await meResponse.text().catch(() => 'no body');
            debugLog('✗ /auth/me failed', errText.substring(0, 100));
          }
        } catch (meError) {
          debugLog('✗ /auth/me exception', meError.message);
        }
      }
      
      if (!currentToken) {
        debugLog('✗✗ NO TOKEN FOUND - CANNOT UPLOAD ✗✗');
        setError('Please log in to upload a photo');
        toast.error('Please log in to upload a photo');
        clearInterval(progressInterval);
        setUploading(false);
        setProgress(0);
        return;
      }
      
      debugLog('--- Starting Upload ---');
      debugLog('Final token for upload', currentToken.substring(0, 30) + '...');
      
      // Create FormData
      const formData = new FormData();
      formData.append('file', croppedBlob, 'profile.jpg');
      debugLog('FormData created with file size', croppedBlob.size + ' bytes');

      // Upload 
      const uploadUrl = `${API}/upload/profile-photo`;
      debugLog('Sending POST to', uploadUrl);
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${currentToken}` },
        credentials: 'include',
        body: formData
      });

      clearInterval(progressInterval);
      setProgress(100);

      debugLog('--- Upload Response ---');
      debugLog('Status', response.status);
      debugLog('StatusText', response.statusText);
      debugLog('OK', response.ok);

      if (response.ok) {
        const data = await response.json();
        debugLog('✓✓ SUCCESS ✓✓', data);
        toast.success('Profile photo updated!');
        setCroppedPreview(null);
        setCroppedBlob(null);
        // Pass the new URL to parent
        onUploadSuccess?.(data.picture || data.avatar_url);
      } else {
        const errorData = await response.json().catch(() => ({}));
        let errorMsg = 'Upload failed';
        
        debugLog('✗ Upload failed');
        debugLog('Error response', errorData);
        
        if (typeof errorData.detail === 'object') {
          errorMsg = errorData.detail.message || errorData.detail.reason || 'Upload failed';
        } else if (typeof errorData.detail === 'string') {
          errorMsg = errorData.detail;
        }
        
        // Add more specific error messages
        if (errorMsg.includes('too small')) {
          errorMsg = 'Image too small - please use an image at least 400x400 pixels';
        } else if (response.status === 413) {
          errorMsg = 'Image too large - maximum file size is 10MB';
        } else if (response.status === 401) {
          debugLog('✗✗ 401 UNAUTHORIZED ✗✗');
          debugLog('Token that failed', currentToken.substring(0, 40) + '...');
          
          // Clear the bad token and ask user to log in again
          localStorage.removeItem('token');
          errorMsg = 'Session expired - please log in again';
        } else if (response.status === 415) {
          errorMsg = 'Invalid image format - please use JPG, PNG, or WebP';
        }
        
        debugLog('Final error message', errorMsg);
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      debugLog('✗✗ EXCEPTION ✗✗', err?.message || 'Unknown error');
      debugLog('Exception name', err?.name);
      debugLog('Exception stack', err?.stack?.substring(0, 200));
      
      // Handle empty error object (common in CORS/network issues)
      let errorMsg = 'Network error. Please try again.';
      if (err && typeof err === 'object' && Object.keys(err).length === 0) {
        debugLog('Empty error object - likely CORS or network issue');
        errorMsg = 'Connection error - please refresh and try again.';
      } else if (err?.message?.includes('NetworkError')) {
        errorMsg = 'Network error - please check your connection.';
      } else if (err?.name === 'AbortError') {
        errorMsg = 'Upload timed out. Please try again.';
      } else if (err?.message) {
        errorMsg = `Upload error: ${err.message}`;
      }
      
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      setProgress(0);
      debugLog('=== UPLOAD COMPLETE ===');
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    setError(null);

    try {
      const headers = getAuthHeaders ? { ...getAuthHeaders(), 'Content-Type': 'application/json' } : {};

      const response = await fetch(`${API}/upload/profile-photo`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        toast.success('Profile photo removed');
        onUploadSuccess?.(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to remove photo');
      }
    } catch (err) {
      console.error('Remove error:', err);
      toast.error('Network error. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  const handleCancel = () => {
    setCroppedPreview(null);
    setCroppedBlob(null);
    setError(null);
  };

  const displayImage = croppedPreview || getDisplayUrl(currentImage);

  return (
    <div className="flex flex-col items-center gap-4" data-testid="profile-photo-uploader">
      {/* Avatar Display */}
      <div className="relative group">
        <div className={`${sizeClasses[size]} rounded-full overflow-hidden bg-muted/50 border-2 border-border`}>
          {displayImage ? (
            <img 
              src={displayImage}
              alt="Profile" 
              className="w-full h-full object-cover"
              onError={(e) => {
                // Hide broken image and show fallback
                e.target.style.display = 'none';
                const fallback = e.target.nextSibling;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-full h-full flex items-center justify-center ${displayImage ? 'hidden' : 'flex'}`}
          >
            <User className="w-1/2 h-1/2 text-muted-foreground" />
          </div>
        </div>

        {/* Camera overlay button - only show when no cropped preview */}
        {!croppedPreview && (
          <label 
            htmlFor="profile-photo-input"
            className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            data-testid="change-photo-overlay"
          >
            <Camera className="w-8 h-8 text-white" />
          </label>
        )}

        {/* Preview indicator */}
        {croppedPreview && (
          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
            <span className="text-xs font-bold">!</span>
          </div>
        )}
      </div>

      {/* Hidden file input - ensure it's clickable */}
      <input
        ref={fileInputRef}
        type="file"
        id="profile-photo-input"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="profile-photo-input"
      />

      {/* Action buttons */}
      {!croppedPreview ? (
        <div className="flex flex-col items-center gap-2">
          <label 
            htmlFor="profile-photo-input"
            className={`inline-flex items-center justify-center ${buttonSizes[size]} font-medium rounded-md bg-secondary hover:bg-secondary/80 text-white cursor-pointer transition-colors`}
            data-testid="change-photo-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Change Photo
          </label>
          
          {/* Remove button */}
          {showRemoveButton && currentImage && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={removing}
              className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
              data-testid="remove-photo-btn"
            >
              {removing ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1" />
              )}
              Remove Photo
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3 w-full max-w-xs">
          {/* Progress bar during upload */}
          {uploading && (
            <div className="space-y-1">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-muted-foreground text-center">Uploading... {progress}%</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <p className="text-sm text-red-400 text-center">{error}</p>
          )}

          {/* Save/Cancel buttons */}
          <div className="flex gap-2 justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={uploading}
              data-testid="cancel-upload-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleUpload}
              disabled={uploading}
              className="bg-secondary hover:bg-secondary/90"
              data-testid="save-photo-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              Save
            </Button>
          </div>
        </div>
      )}

      {/* Helper text */}
      <p className="text-xs text-muted-foreground text-center">
        JPG, PNG, or WEBP • Max 10MB
      </p>

      {/* DEBUG PANEL - Shows on-screen debug information */}
      {DEBUG_MODE && (
        <div className="w-full max-w-md mt-4" data-testid="debug-panel">
          <button
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            className="flex items-center gap-2 text-xs text-amber-500 hover:text-amber-400 mb-2"
          >
            <Bug className="w-3 h-3" />
            {showDebugPanel ? 'Hide' : 'Show'} Debug Info
          </button>
          
          {showDebugPanel && (
            <div className="bg-background/80 border border-amber-500/50 rounded-lg p-3 text-xs font-mono overflow-x-auto">
              <div className="text-amber-400 font-bold mb-2">
                🐛 Debug Panel (Screenshot this if error occurs)
              </div>
              <div className="space-y-1 text-green-400">
                {debugLogs.length === 0 ? (
                  <div className="text-gray-400">No logs yet. Click "Save" to start upload.</div>
                ) : (
                  debugLogs.map((log, i) => (
                    <div key={i} className={`${log.includes('✗') ? 'text-red-400' : log.includes('✓') ? 'text-green-400' : 'text-gray-300'}`}>
                      {log}
                    </div>
                  ))
                )}
              </div>
              <div className="mt-2 pt-2 border-t border-amber-500/30 text-gray-400">
                <div>Host: {typeof window !== 'undefined' ? window.location.hostname : 'N/A'}</div>
                <div>API: {API || '(same origin)'}</div>
                <div>Full API path: {API}/upload/profile-photo</div>
                <div>LocalStorage token: {typeof localStorage !== 'undefined' && localStorage.getItem('token') ? 'Present' : 'Missing'}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Crop Modal */}
      <ImageCropModal
        isOpen={showCropModal}
        onClose={handleCloseCropModal}
        imageSrc={rawImage}
        onCropComplete={handleCropComplete}
      />
    </div>
  );
}
